import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  ChartBar,
  Calendar,
  Pencil,
  FilePdf,
  Upload,
  Check,
  X,
  Warehouse,
} from '@phosphor-icons/react';
import { Trash2, Package, FileUp, Sparkles } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const categoryLabels = {
  temizlik: { label: 'Temizlik', color: 'bg-blue-100 text-blue-800' },
  icecek: { label: 'İçecek', color: 'bg-purple-100 text-purple-800' },
  ambalaj: { label: 'Ambalaj', color: 'bg-yellow-100 text-yellow-800' },
  mutfak: { label: 'Mutfak', color: 'bg-orange-100 text-orange-800' },
  ofis: { label: 'Ofis', color: 'bg-gray-100 text-gray-800' },
  diger: { label: 'Diğer', color: 'bg-slate-100 text-slate-800' },
};

const unitLabels = {
  adet: 'Adet',
  kg: 'Kg',
  lt: 'Litre',
  koli: 'Koli',
  paket: 'Paket',
};

export default function GeneralProducts() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  
  // Tarih filtreleri
  const [filterMode, setFilterMode] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const [suggestions, setSuggestions] = useState({ products: [], suppliers: [] });
  const [activeTab, setActiveTab] = useState('list');
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    product_name: '',
    category: 'diger',
    quantity: '',
    unit: 'adet',
    unit_price: '',
    supplier: '',
    invoice_no: '',
    notes: '',
    add_to_stock: false,
  });
  
  // PDF Import
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfDate, setPdfDate] = useState(new Date().toISOString().split('T')[0]);
  const [pdfSupplier, setPdfSupplier] = useState('');
  const [pdfAddToStock, setPdfAddToStock] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchData();
    fetchSuggestions();
  }, [selectedMonth, startDate, endDate, filterMode]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = '/general';
      let reportUrl = '/general/report';
      
      if (filterMode === 'range' && startDate && endDate) {
        url += `?start_date=${startDate}&end_date=${endDate}`;
        reportUrl += `?start_date=${startDate}&end_date=${endDate}`;
      } else {
        url += `?month=${selectedMonth}`;
        reportUrl += `?month=${selectedMonth}`;
      }
      
      const [entriesRes, reportRes] = await Promise.all([
        api.get(url),
        api.get(reportUrl)
      ]);
      setEntries(entriesRes.data);
      setReport(reportRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const res = await api.get('/general/suggestions');
      setSuggestions(res.data);
    } catch (error) {
      console.error('Suggestions fetch error:', error);
    }
  };

  const resetForm = () => {
    setEditingEntry(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      product_name: '',
      category: 'diger',
      quantity: '',
      unit: 'adet',
      unit_price: '',
      supplier: '',
      invoice_no: '',
      notes: '',
      add_to_stock: false,
    });
  };

  const handleSubmit = async () => {
    if (!formData.product_name || !formData.quantity || !formData.unit_price) {
      toast.error('Ürün adı, miktar ve birim fiyat gerekli');
      return;
    }

    try {
      const payload = {
        ...formData,
        quantity: parseFloat(formData.quantity),
        unit_price: parseFloat(formData.unit_price),
        total_amount: parseFloat(formData.quantity) * parseFloat(formData.unit_price),
      };

      if (editingEntry) {
        await api.put(`/general/${editingEntry.id}`, payload);
        toast.success('Kayıt güncellendi');
      } else {
        await api.post('/general', payload);
        const stockMsg = formData.add_to_stock ? ' ve depo stoğuna eklendi' : '';
        toast.success(`Kayıt eklendi${stockMsg}`);
      }

      setIsDialogOpen(false);
      resetForm();
      fetchData();
      fetchSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      date: entry.date,
      product_name: entry.product_name,
      category: entry.category,
      quantity: entry.quantity.toString(),
      unit: entry.unit,
      unit_price: entry.unit_price.toString(),
      supplier: entry.supplier || '',
      invoice_no: entry.invoice_no || '',
      notes: entry.notes || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/general/${id}`);
      toast.success('Kayıt silindi');
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  // PDF Import
  const handlePdfFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Sadece PDF dosyaları kabul edilir');
      return;
    }
    
    setPdfFile(file);
    setPdfPreview(null);
    setPdfLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/general/preview-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPdfPreview(res.data);
      if (res.data.summary?.detected_supplier) {
        setPdfSupplier(res.data.summary.detected_supplier);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'PDF okunamadı');
      setPdfFile(null);
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePdfImport = async () => {
    if (!pdfFile) return;
    
    setPdfLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', pdfFile);
      
      const params = new URLSearchParams();
      params.append('date', pdfDate);
      if (pdfSupplier) params.append('supplier', pdfSupplier);
      params.append('add_to_stock', pdfAddToStock.toString());
      params.append('use_ai', 'true');
      
      const res = await api.post(`/general/import-pdf?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const aiMsg = res.data.ai_used ? ' (AI)' : '';
      const stockMsg = pdfAddToStock ? ' ve depo stoğuna eklendi' : '';
      toast.success(`${res.data.summary.entry_count} ürün aktarıldı${aiMsg}${stockMsg}! Toplam: ₺${res.data.summary.total_amount.toLocaleString('tr-TR')}`);
      setIsPdfDialogOpen(false);
      resetPdfDialog();
      fetchData();
      fetchSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'PDF aktarımı başarısız');
    } finally {
      setPdfLoading(false);
    }
  };

  const resetPdfDialog = () => {
    setPdfFile(null);
    setPdfPreview(null);
    setPdfSupplier('');
    setPdfDate(new Date().toISOString().split('T')[0]);
    setPdfAddToStock(false);
  };

  const formatCurrency = (value) => {
    return `₺${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(value || 0)}`;
  };

  const getCategoryBadge = (category) => {
    const cat = categoryLabels[category] || { label: category, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={cat.color}>{cat.label}</Badge>;
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="general-products">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Package className="w-8 h-8 text-indigo-600" />
              {t('generalProducts.title')}
            </h1>
            <p className="text-muted-foreground">{t('generalProducts.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Filtre Modu */}
            <div className="flex items-center gap-2 border rounded-lg p-1">
              <Button
                variant={filterMode === 'month' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setFilterMode('month')}
                data-testid="filter-mode-month"
              >
                {t('common.month')}
              </Button>
              <Button
                variant={filterMode === 'range' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setFilterMode('range')}
                data-testid="filter-mode-range"
              >
                {t('common.dateRange')}
              </Button>
            </div>
            
            {filterMode === 'month' ? (
              <Input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-40"
                data-testid="month-filter"
              />
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-36"
                  data-testid="start-date-filter"
                />
                <span className="text-muted-foreground">-</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-36"
                  data-testid="end-date-filter"
                />
              </div>
            )}
            
            {isManager && (
              <>
                {/* PDF'den Aktar */}
                <Dialog open={isPdfDialogOpen} onOpenChange={(open) => { setIsPdfDialogOpen(open); if (!open) resetPdfDialog(); }}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-indigo-300 text-indigo-600 hover:bg-indigo-50" data-testid="pdf-import-btn">
                      <FilePdf className="w-4 h-4 mr-2" />
                      PDF'den Aktar
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <FilePdf className="w-5 h-5 text-indigo-600" />
                        Genel Ürünler PDF Fatura Aktarımı
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      {/* Dosya Seçimi */}
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={handlePdfFileSelect}
                          className="hidden"
                          id="general-pdf-upload"
                          data-testid="pdf-file-input"
                        />
                        <label htmlFor="general-pdf-upload" className="cursor-pointer">
                          {pdfFile ? (
                            <div className="flex items-center justify-center gap-3">
                              <FilePdf className="w-10 h-10 text-indigo-500" />
                              <div className="text-left">
                                <p className="font-medium">{pdfFile.name}</p>
                                <p className="text-sm text-muted-foreground">{(pdfFile.size / 1024).toFixed(1)} KB</p>
                              </div>
                              <Button type="button" variant="ghost" size="sm" onClick={(e) => { e.preventDefault(); setPdfFile(null); setPdfPreview(null); }}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div>
                              <FileUp className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                              <p className="font-medium">Faturayı seçin veya sürükleyin</p>
                              <p className="text-sm text-muted-foreground mt-1">Temizlik, içecek, ambalaj faturaları</p>
                            </div>
                          )}
                        </label>
                      </div>
                      
                      {/* Tarih, Tedarikçi */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Fatura Tarihi</Label>
                          <Input type="date" value={pdfDate} onChange={(e) => setPdfDate(e.target.value)} data-testid="pdf-date-input" />
                        </div>
                        <div>
                          <Label>Tedarikçi</Label>
                          <Input value={pdfSupplier} onChange={(e) => setPdfSupplier(e.target.value)} placeholder="Otomatik algılanır" data-testid="pdf-supplier-input" />
                        </div>
                      </div>
                      
                      {pdfLoading && (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mr-3"></div>
                          <span>PDF okunuyor...</span>
                        </div>
                      )}
                      
                      {pdfPreview && !pdfLoading && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between bg-indigo-50 p-3 rounded-lg border border-indigo-200">
                            <div className="flex items-center gap-2">
                              <Check className="w-5 h-5 text-indigo-600" />
                              <span className="font-medium text-indigo-700">{pdfPreview.summary.entry_count} ürün bulundu</span>
                            </div>
                            <span className="text-lg font-bold text-indigo-700">{formatCurrency(pdfPreview.summary.total_amount)}</span>
                          </div>
                          
                          <div className="max-h-64 overflow-y-auto border rounded-lg">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Ürün</TableHead>
                                  <TableHead>Kategori</TableHead>
                                  <TableHead className="text-right">Miktar</TableHead>
                                  <TableHead className="text-right">Birim Fiyat</TableHead>
                                  <TableHead className="text-right">Toplam</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {pdfPreview.items.map((item, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell className="font-medium">{item.product_name}</TableCell>
                                    <TableCell>{getCategoryBadge(item.category)}</TableCell>
                                    <TableCell className="text-right">{item.quantity} {unitLabels[item.unit] || item.unit}</TableCell>
                                    <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                                    <TableCell className="text-right font-bold">{formatCurrency(item.total_amount)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}
                      
                      {/* Depo Stoğuna Ekle */}
                      <div className="flex items-center space-x-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <Checkbox
                          id="pdf-add-to-stock"
                          checked={pdfAddToStock}
                          onCheckedChange={(checked) => setPdfAddToStock(checked)}
                          data-testid="pdf-add-to-stock"
                        />
                        <div className="flex items-center gap-2">
                          <Warehouse className="w-5 h-5 text-emerald-600" />
                          <Label htmlFor="pdf-add-to-stock" className="cursor-pointer font-medium text-emerald-700">
                            Depo Stoğuna Ekle & Fiyat Güncelle
                          </Label>
                        </div>
                      </div>
                      
                      {pdfPreview && (
                        <div className="flex items-center gap-2 text-sm text-indigo-600">
                          <Sparkles className="w-4 h-4" />
                          <span>AI destekli akıllı fatura okuma aktif</span>
                        </div>
                      )}
                      
                      <div className="flex gap-3 pt-2">
                        <Button variant="outline" className="flex-1" onClick={() => { setIsPdfDialogOpen(false); resetPdfDialog(); }}>İptal</Button>
                        <Button onClick={handlePdfImport} disabled={!pdfPreview || pdfLoading} className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="pdf-import-submit">
                          <Upload className="w-4 h-4 mr-2" />
                          {pdfPreview?.summary?.entry_count || 0} Ürünü Kaydet
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                
                {/* Manuel Ekleme */}
                <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
                  <DialogTrigger asChild>
                    <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-entry-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Ürün Ekle
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{editingEntry ? 'Ürün Düzenle' : 'Yeni Ürün Girişi'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto pr-2">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Tarih *</Label>
                          <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} data-testid="entry-date" />
                        </div>
                        <div>
                          <Label>Fatura No</Label>
                          <Input value={formData.invoice_no} onChange={(e) => setFormData({ ...formData, invoice_no: e.target.value })} placeholder="Opsiyonel" data-testid="entry-invoice" />
                        </div>
                      </div>
                      
                      <div>
                        <Label>Ürün Adı *</Label>
                        <Input
                          value={formData.product_name}
                          onChange={(e) => setFormData({ ...formData, product_name: e.target.value.toUpperCase() })}
                          placeholder="DETERJAN, KOLA, KARTON KUTU vb."
                          list="general-products"
                          data-testid="entry-product-name"
                        />
                        <datalist id="general-products">
                          {suggestions.products.map((p, i) => (
                            <option key={i} value={p.name} />
                          ))}
                        </datalist>
                      </div>
                      
                      <div>
                        <Label>Kategori *</Label>
                        <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                          <SelectTrigger data-testid="entry-category">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="temizlik">Temizlik</SelectItem>
                            <SelectItem value="icecek">İçecek</SelectItem>
                            <SelectItem value="ambalaj">Ambalaj</SelectItem>
                            <SelectItem value="mutfak">Mutfak Malzemesi</SelectItem>
                            <SelectItem value="ofis">Ofis/Kırtasiye</SelectItem>
                            <SelectItem value="diger">Diğer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Miktar *</Label>
                          <Input type="number" step="0.01" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} placeholder="10" data-testid="entry-quantity" />
                        </div>
                        <div>
                          <Label>Birim</Label>
                          <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                            <SelectTrigger data-testid="entry-unit">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="adet">Adet</SelectItem>
                              <SelectItem value="kg">Kg</SelectItem>
                              <SelectItem value="lt">Litre</SelectItem>
                              <SelectItem value="koli">Koli</SelectItem>
                              <SelectItem value="paket">Paket</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Birim Fiyat (₺) *</Label>
                          <Input type="number" step="0.01" value={formData.unit_price} onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })} placeholder="25" data-testid="entry-unit-price" />
                        </div>
                      </div>
                      
                      {formData.quantity && formData.unit_price && (
                        <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-center">
                          <span className="text-sm text-indigo-700">Toplam:</span>
                          <span className="text-xl font-bold text-indigo-700 ml-2">
                            {formatCurrency(parseFloat(formData.quantity) * parseFloat(formData.unit_price))}
                          </span>
                        </div>
                      )}
                      
                      <div>
                        <Label>Tedarikçi</Label>
                        <Input value={formData.supplier} onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} placeholder="Tedarikçi adı" list="general-suppliers" data-testid="entry-supplier" />
                        <datalist id="general-suppliers">
                          {suggestions.suppliers.map((s, i) => (
                            <option key={i} value={s} />
                          ))}
                        </datalist>
                      </div>
                      
                      <div>
                        <Label>Not</Label>
                        <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Opsiyonel not" data-testid="entry-notes" />
                      </div>
                      
                      {/* Depo Stoğuna Ekle - Sadece yeni kayıtta */}
                      {!editingEntry && (
                        <div className="flex items-center space-x-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <Checkbox
                            id="form-add-to-stock"
                            checked={formData.add_to_stock}
                            onCheckedChange={(checked) => setFormData({ ...formData, add_to_stock: checked })}
                            data-testid="form-add-to-stock"
                          />
                          <div className="flex items-center gap-2">
                            <Warehouse className="w-5 h-5 text-emerald-600" />
                            <Label htmlFor="form-add-to-stock" className="cursor-pointer font-medium text-emerald-700">
                              Depo Stoğuna Ekle & Fiyat Güncelle
                            </Label>
                          </div>
                        </div>
                      )}
                      
                      <Button onClick={handleSubmit} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="entry-submit">
                        {editingEntry ? 'Güncelle' : 'Kaydet'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>

        {/* Özet Kartı */}
        {report && (
          <Card className="border-2 border-indigo-200 bg-indigo-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-indigo-700 font-medium uppercase">Toplam Harcama</p>
                  <p className="text-3xl font-bold text-indigo-700">{formatCurrency(report.total_amount)}</p>
                  <p className="text-xs text-indigo-600">{report.total_entries} kayıt</p>
                </div>
                <div className="p-3 bg-indigo-200 rounded-full">
                  <Package className="w-8 h-8 text-indigo-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tab'lar */}
        <div className="flex gap-2 border-b">
          <Button variant={activeTab === 'list' ? 'default' : 'ghost'} onClick={() => setActiveTab('list')} className="rounded-b-none" data-testid="tab-list">
            <Calendar className="w-4 h-4 mr-2" />
            Giriş Listesi
          </Button>
          <Button variant={activeTab === 'report' ? 'default' : 'ghost'} onClick={() => setActiveTab('report')} className="rounded-b-none" data-testid="tab-report">
            <ChartBar className="w-4 h-4 mr-2" />
            Raporlar
          </Button>
        </div>

        {/* Giriş Listesi */}
        {activeTab === 'list' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{getMonthName(selectedMonth)} Genel Ürün Girişleri ({entries.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : entries.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package size={48} className="mx-auto mb-3 opacity-50" />
                  <p>Bu ayda genel ürün girişi yok</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Ürün</TableHead>
                        <TableHead>Kategori</TableHead>
                        <TableHead className="text-right">Miktar</TableHead>
                        <TableHead className="text-right">Birim Fiyat</TableHead>
                        <TableHead className="text-right">Toplam</TableHead>
                        <TableHead>Tedarikçi</TableHead>
                        <TableHead className="text-center">İşlem</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map((entry) => (
                        <TableRow key={entry.id} data-testid={`entry-row-${entry.id}`}>
                          <TableCell className="font-medium">{new Date(entry.date).toLocaleDateString('tr-TR')}</TableCell>
                          <TableCell className="font-medium">{entry.product_name}</TableCell>
                          <TableCell>{getCategoryBadge(entry.category)}</TableCell>
                          <TableCell className="text-right">{entry.quantity} {unitLabels[entry.unit] || entry.unit}</TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.unit_price)}</TableCell>
                          <TableCell className="text-right font-bold text-indigo-600">{formatCurrency(entry.total_amount)}</TableCell>
                          <TableCell className="text-muted-foreground">{entry.supplier || '-'}</TableCell>
                          <TableCell>
                            <div className="flex justify-center gap-1">
                              {isManager && (
                                <Button variant="ghost" size="sm" onClick={() => handleEdit(entry)} data-testid={`edit-btn-${entry.id}`}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              )}
                              {isAdmin && (
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.id)} className="text-destructive hover:text-destructive" data-testid={`delete-btn-${entry.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Raporlar */}
        {activeTab === 'report' && report && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Kategori Bazlı */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Kategori Bazlı Harcama</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {report.by_category.map((cat, idx) => {
                    const percentage = report.total_amount > 0 ? (cat.total_amount / report.total_amount * 100) : 0;
                    return (
                      <div key={idx} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">{getCategoryBadge(cat.category)}</span>
                          <span className="text-muted-foreground">{formatCurrency(cat.total_amount)} (%{percentage.toFixed(1)})</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${percentage}%` }} />
                        </div>
                        <p className="text-xs text-muted-foreground">{cat.entry_count} kayıt</p>
                      </div>
                    );
                  })}
                  {report.by_category.length === 0 && (
                    <p className="text-center text-muted-foreground py-4">Veri yok</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Ürün Bazlı */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">En Çok Alınan Ürünler</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {report.by_product.slice(0, 10).map((prod, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                      <div>
                        <span className="font-medium">{prod.product_name}</span>
                        <span className="text-xs text-muted-foreground ml-2">({prod.total_quantity} {unitLabels[prod.unit] || prod.unit})</span>
                      </div>
                      <span className="font-bold text-indigo-600">{formatCurrency(prod.total_amount)}</span>
                    </div>
                  ))}
                  {report.by_product.length === 0 && (
                    <p className="text-center text-muted-foreground py-4">Veri yok</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
